/**
 * InstrumentationGuideModal - Code examples for PII detection integration
 * Shows Python SDK, JavaScript SDK, and direct API usage examples
 */
import React, { useState } from 'react';
import { Modal, Tabs, Alert, Typography } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const { Paragraph, Text } = Typography;

function InstrumentationGuideModal({ open, onClose, projectId, project }) {
  const [copiedTab, setCopiedTab] = useState(null);

  const handleCopy = (text, tabKey) => {
    navigator.clipboard.writeText(text);
    setCopiedTab(tabKey);
    setTimeout(() => setCopiedTab(null), 2000);
  };

  // Code examples
  const pythonCode = `# Install TrustGrid Python SDK
pip install trustgrid-python-sdk

# Import and configure
from trustgrid import TrustGridClient, PIIDetector

client = TrustGridClient(
    project_id=${projectId || 'YOUR_PROJECT_ID'},
    api_key="YOUR_API_KEY"
)

# Method 1: Auto-detect PII with built-in scanner
pii_detector = PIIDetector()
text_to_scan = "Contact John Doe at john@example.com or 555-123-4567"
pii_result = pii_detector.scan(text_to_scan)

# Ingest event with PII detection
client.ingest_event({
    "event_type": "inference",
    "provider": "openai",
    "model": "gpt-4",
    "ts": datetime.utcnow().isoformat(),
    "pii_detected": pii_result['pii_detected'],
    "pii_types": pii_result['pii_types'],
    "masking_enabled": False,  # Set to True if you masked PII
    "event_metadata": {
        "prompt": text_to_scan,
        "response": "..."
    }
})

# Method 2: Manual flagging (if you have your own PII detection)
client.ingest_event({
    "event_type": "inference",
    "provider": "anthropic",
    "model": "claude-3",
    "ts": datetime.utcnow().isoformat(),
    "pii_detected": True,
    "pii_types": ["email", "phone"],
    "masking_enabled": True,  # You masked it
    "event_metadata": {
        "request_id": "abc123"
    }
})`;

  const javascriptCode = `// Install TrustGrid JavaScript SDK
npm install @trustgrid/sdk

// Import and configure
import { TrustGridClient, PIIDetector } from '@trustgrid/sdk';

const client = new TrustGridClient({
  projectId: ${projectId || 'YOUR_PROJECT_ID'},
  apiKey: 'YOUR_API_KEY'
});

// Method 1: Auto-detect PII with built-in scanner
const piiDetector = new PIIDetector();
const textToScan = "Contact John Doe at john@example.com or 555-123-4567";
const piiResult = piiDetector.scan(textToScan);

// Ingest event with PII detection
await client.ingestEvent({
  event_type: "inference",
  provider: "openai",
  model: "gpt-4",
  ts: new Date().toISOString(),
  pii_detected: piiResult.pii_detected,
  pii_types: piiResult.pii_types,
  masking_enabled: false,  // Set to true if you masked PII
  event_metadata: {
    prompt: textToScan,
    response: "..."
  }
});

// Method 2: Manual flagging (if you have your own PII detection)
await client.ingestEvent({
  event_type: "inference",
  provider: "anthropic",
  model: "claude-3",
  ts: new Date().toISOString(),
  pii_detected: true,
  pii_types: ["email", "phone"],
  masking_enabled: true,  // You masked it
  event_metadata: {
    request_id: "abc123"
  }
});`;

  const apiCode = `# Direct API Usage (no SDK)
# POST /api/trustgrid/v1/projects/${projectId || 'YOUR_PROJECT_ID'}/events/batch

curl -X POST \\
  https://api.truvex.com/api/trustgrid/v1/projects/${projectId || 'YOUR_PROJECT_ID'}/events/batch \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -d '{
    "events": [
      {
        "event_type": "inference",
        "provider": "openai",
        "model": "gpt-4",
        "ts": "2025-11-03T12:00:00Z",
        "pii_detected": true,
        "pii_types": ["email", "phone"],
        "masking_enabled": false,
        "event_metadata": {
          "prompt": "Contact details included...",
          "tokens_used": 150
        }
      }
    ]
  }'

# Response:
# {
#   "message": "Ingested 1 events",
#   "event_ids": [12345],
#   "pii_scans_queued": 0
# }`;

  const maskingCode = `# Example: Masking PII before sending to AI model

from trustgrid import PIIDetector, PIIMasker

detector = PIIDetector()
masker = PIIMasker()

# Original text with PII
original_text = "My email is john@example.com and phone is 555-123-4567"

# Detect PII
pii_result = detector.scan(original_text)
# Returns: {
#   'pii_detected': True,
#   'pii_types': ['email', 'phone'],
#   'matches': [...]
# }

# Mask PII for AI processing
masked_text = masker.mask(original_text, pii_result['matches'])
# Returns: "My email is [EMAIL_REDACTED] and phone is [PHONE_REDACTED]"

# Send masked text to AI model
response = openai.ChatCompletion.create(
    model="gpt-4",
    messages=[{"role": "user", "content": masked_text}]
)

# Ingest event to TrustGrid
client.ingest_event({
    "event_type": "inference",
    "provider": "openai",
    "model": "gpt-4",
    "ts": datetime.utcnow().isoformat(),
    "pii_detected": True,
    "pii_types": pii_result['pii_types'],
    "masking_enabled": True,  # We masked it!
    "event_metadata": {
        "prompt_length": len(masked_text),
        "pii_count": len(pii_result['matches'])
    }
})`;

  const tabs = [
    {
      key: '1',
      label: '🐍 Python SDK',
      content: pythonCode,
      language: 'python',
      description: 'Recommended for Python-based AI/ML applications'
    },
    {
      key: '2',
      label: '⚡ JavaScript SDK',
      content: javascriptCode,
      language: 'javascript',
      description: 'Recommended for Node.js and web applications'
    },
    {
      key: '3',
      label: '🔌 Direct API',
      content: apiCode,
      language: 'bash',
      description: 'Use raw HTTP API for any language'
    },
    {
      key: '4',
      label: '🎭 PII Masking Example',
      content: maskingCode,
      language: 'python',
      description: 'How to mask PII before sending to AI models'
    }
  ];

  return (
    <Modal
      title="📚 PII Detection Implementation Guide"
      open={open}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      <Alert
        type="info"
        message="Three Ways to Implement"
        description={
          <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
            <li><strong>Let TrustGrid Scan:</strong> Enable scanner mode and we'll detect PII in event metadata</li>
            <li><strong>Use SDK Detection:</strong> Use our PIIDetector in your code to detect before sending events</li>
            <li><strong>Manual Flagging:</strong> If you have your own detection, just send the flags with events</li>
          </ul>
        }
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <Tabs
        items={tabs.map(tab => ({
          key: tab.key,
          label: tab.label,
          children: (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <Text type="secondary">{tab.description}</Text>
                <button
                  className="custom-button"
                  onClick={() => handleCopy(tab.content, tab.key)}
                  style={{ padding: '4px 12px' }}
                >
                  <CopyOutlined /> {copiedTab === tab.key ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
              <SyntaxHighlighter
                language={tab.language}
                style={vscDarkPlus}
                customStyle={{
                  borderRadius: '6px',
                  fontSize: '13px',
                  maxHeight: '400px'
                }}
              >
                {tab.content}
              </SyntaxHighlighter>
            </div>
          )
        }))}
      />

      <Alert
        type="success"
        message="Best Practice: Detection Only"
        description={
          <div>
            <p>TrustGrid focuses on <strong>detection</strong>, not masking. Here's the recommended flow:</p>
            <ol style={{ marginTop: '8px', marginLeft: '16px' }}>
              <li>Your application detects/masks PII (using our SDK or your own tool)</li>
              <li>Send masked data to AI models</li>
              <li>Log events to TrustGrid with <code>pii_detected=True, masking_enabled=True</code></li>
              <li>TrustGrid verifies all PII was masked before AI processing</li>
            </ol>
          </div>
        }
        showIcon
        style={{ marginTop: '16px' }}
      />

      <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
        <strong>📖 Additional Resources:</strong>
        <ul style={{ marginTop: '8px', marginBottom: 0 }}>
          <li><a href="https://docs.trustgrid.com/sdk/python" target="_blank" rel="noopener noreferrer">Python SDK Documentation</a></li>
          <li><a href="https://docs.trustgrid.com/sdk/javascript" target="_blank" rel="noopener noreferrer">JavaScript SDK Documentation</a></li>
          <li><a href="https://docs.trustgrid.com/api/events" target="_blank" rel="noopener noreferrer">API Reference</a></li>
          <li><a href="https://docs.trustgrid.com/guides/pii-detection" target="_blank" rel="noopener noreferrer">PII Detection Best Practices</a></li>
        </ul>
      </div>
    </Modal>
  );
}

export default InstrumentationGuideModal;
